import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ScoreBadge, SourceTag, formatDate, daysUntil, formatGBP } from "../lib/format";
import { Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import CpvCombobox from "../components/CpvCombobox";

const PAGE_SIZE = 25;

// Grouped source list — labels mirror what users see across the app.
// Comma-separated values are passed through to /notices?source=… which
// the backend treats as a $in query. That lets us merge related feeds
// (e.g. Contracts Finder OCDS + keyword) under a single user-facing label.
const SOURCE_GROUPS = [
  {
    region: "UK",
    sources: [
      ["find_a_tender", "Find a Tender"],
      ["contracts_finder,contracts_finder_market", "Contracts Finder"],
      ["award_notices", "Awarded Contracts (UK)"],
      ["public_contracts_scotland", "Public Contracts Scotland"],
      ["sell2wales", "Sell2Wales"],
      ["etenders_ni", "eTendersNI"],
      ["digital_marketplace", "Digital Marketplace (CCS)"],
    ],
  },
  { region: "EU", sources: [["ted_europa", "TED Europa"]] },
  {
    region: "NATO",
    sources: [
      ["nato_nspa", "NATO NSPA"],
      ["occar", "OCCAR"],
    ],
  },
  { region: "US", sources: [["sam_gov", "SAM.gov (US Federal)"]] },
  {
    region: "FVEY",
    sources: [
      ["austender", "AusTender (Australia)"],
      ["canadabuys", "CanadaBuys (Canada DND)"],
      ["gets_nz", "GETS NZ (New Zealand)"],
    ],
  },
];

const REGION_LABEL = {
  UK: "United Kingdom",
  EU: "European Union",
  NATO: "NATO",
  US: "United States",
  FVEY: "Five Eyes (Aus / Can / NZ)",
};

const STAGE_OPTIONS = [
  ["", "All stages"],
  ["tender", "Tender"],
  ["award", "Awarded"],
  ["planning", "Planning"],
  ["pin", "Prior information notice"],
];

const SORT_OPTIONS = [
  ["published_desc", "Newest first"],
  ["deadline_asc", "Deadline soonest"],
  ["value_desc", "Highest value"],
];

export default function Opportunities() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [matchMode, setMatchMode] = useState("exact");
  const [searchTokens, setSearchTokens] = useState([]);

  // All filter state is seeded from the URL so the back button (or a shared
  // link / browser refresh) restores exactly the search context the user had.
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [region, setRegion] = useState(searchParams.get("region") || "");
  const [source, setSource] = useState(searchParams.get("source") || "");
  const [stage, setStage] = useState(searchParams.get("stage") || "");
  const [cpv, setCpv] = useState(searchParams.get("cpv") || "");
  const [minScore, setMinScore] = useState(parseInt(searchParams.get("min_score") || "0", 10) || 0);
  const [sort, setSort] = useState(searchParams.get("sort") || "published_desc");
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10) || 1);

  const offset = (page - 1) * PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Keep the URL in sync with filter state. `replace: true` so each keystroke
  // doesn't pollute browser history — the back button still returns the user
  // to whatever page they were on *before* the Opportunities page, while
  // landing back here re-hydrates the full search context from the URL.
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (region) next.set("region", region);
    if (source) next.set("source", source);
    if (stage) next.set("stage", stage);
    if (cpv) next.set("cpv", cpv);
    if (minScore) next.set("min_score", String(minScore));
    if (sort && sort !== "published_desc") next.set("sort", sort);
    if (page > 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [q, region, source, stage, cpv, minScore, sort, page, setSearchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notices", {
        params: {
          q: q || undefined,
          source: source || undefined,
          region: region || undefined,
          min_score: minScore || undefined,
          stage: stage || undefined,
          cpv: cpv || undefined,
          sort,
          limit: PAGE_SIZE,
          offset,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setMatchMode(data.match_mode || "exact");
      setSearchTokens(data.search_tokens || []);
    } finally {
      setLoading(false);
    }
  }, [q, region, source, minScore, stage, cpv, sort, offset]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const reset = () => {
    setQ(""); setRegion(""); setSource(""); setMinScore(0);
    setStage(""); setCpv(""); setSort("published_desc"); setPage(1);
  };

  // When region changes, clear narrower source filter
  const onRegionChange = (r) => {
    setRegion(r);
    setSource("");
    setPage(1);
  };

  const visibleSourceGroups = region
    ? SOURCE_GROUPS.filter((g) => g.region === region)
    : SOURCE_GROUPS;

  return (
    <div data-testid="opportunities-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-meta">Opportunities</div>
          <div className="display">All notices</div>
          <p className="text-[13px] mt-1" style={{ color: "var(--parapet-text-secondary)" }}>
            Live UK / EU / NATO / US / FVEY notices. {PAGE_SIZE} per page. Click any row for source provenance.
          </p>
        </div>
        <div className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>
          <span className="value-mono">{total.toLocaleString()}</span> notices
        </div>
      </div>

      <div className="p-card-tight mb-4" data-testid="opportunities-filter-bar">
        <div className="grid gap-2" style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1.5fr 1fr auto" }}>
          {/* Search */}
          <div className="relative">
            <Search size={14} style={{ position: "absolute", left: 10, top: 11, color: "var(--parapet-text-secondary)" }} />
            <input className="p-input w-full" style={{ paddingLeft: 32 }} value={q}
                   onChange={(e) => { setQ(e.target.value); setPage(1); }}
                   placeholder="Search title, buyer, description…"
                   data-testid="opportunities-search" />
          </div>

          {/* Region */}
          <select className="p-input" value={region}
                  onChange={(e) => onRegionChange(e.target.value)}
                  data-testid="opportunities-region-select">
            <option value="">All regions</option>
            {Object.entries(REGION_LABEL).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>

          {/* Source (filtered by region) */}
          <select className="p-input" value={source}
                  onChange={(e) => { setSource(e.target.value); setPage(1); }}
                  data-testid="opportunities-source-select">
            <option value="">All sources{region ? ` (${region})` : ""}</option>
            {visibleSourceGroups.map((g) => (
              <optgroup key={g.region} label={REGION_LABEL[g.region] || g.region}>
                {g.sources.map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </optgroup>
            ))}
          </select>

          {/* Stage */}
          <select className="p-input" value={stage}
                  onChange={(e) => { setStage(e.target.value); setPage(1); }}
                  data-testid="opportunities-stage-select">
            {STAGE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>

          {/* CPV */}
          <CpvCombobox
            mode="single"
            value={cpv}
            onChange={(c) => { setCpv(c); setPage(1); }}
            placeholder="All CPV codes"
            data-testid="opportunities-cpv-combobox"
          />

          {/* Sort */}
          <select className="p-input" value={sort}
                  onChange={(e) => { setSort(e.target.value); setPage(1); }}
                  data-testid="opportunities-sort-select">
            {SORT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>

          {/* Reset */}
          <button onClick={reset} className="btn-ghost" data-testid="opportunities-reset">
            <X size={12} /> Reset
          </button>
        </div>

        {/* Min fit slider — separate row */}
        <div className="flex items-center gap-3 mt-3 pt-3" style={{ borderTop: "1px solid var(--parapet-border)" }}>
          <span className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>Min fit score</span>
          <input type="range" min="0" max="100" value={minScore} style={{ width: 200 }}
                 onChange={(e) => { setMinScore(parseInt(e.target.value)); setPage(1); }}
                 data-testid="opportunities-min-score" />
          <span className="value-mono w-10 text-right">{minScore}</span>
          <span className="text-[12px] ml-4" style={{ color: "var(--parapet-text-secondary)" }}>
            Page <span className="value-mono">{page}</span> of <span className="value-mono">{totalPages}</span>
          </span>
        </div>
      </div>

      <div className="p-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="p-8 text-center"><span className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state m-6">
            <h3>No opportunities match</h3>
            <p>Adjust filters or wait for the next ingestion cycle.</p>
          </div>
        ) : (
          <>
            {matchMode === "or" && searchTokens.length > 1 && (
              <div
                className="text-[12px] px-4 py-2"
                style={{
                  background: "#FFF6E5",
                  borderBottom: "1px solid var(--parapet-border)",
                  color: "#7a4a02",
                }}
                data-testid="opportunities-broader-results-banner"
              >
                No notice matches <b>all</b> of <span className="value-mono">{searchTokens.join(" + ")}</span>.
                Showing rows matching <b>any</b> of those words.
              </div>
            )}
          <table className="p-table">
            <thead>
              <tr>
                <th style={{ width: 64 }}>Fit</th>
                <th>Title</th>
                <th style={{ width: 90 }}>Region</th>
                <th style={{ width: 200 }}>Buyer</th>
                <th style={{ width: 130 }}>Value</th>
                <th style={{ width: 110 }}>Stage</th>
                <th style={{ width: 130 }}>Deadline</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} data-testid={`opportunity-row-${n.id}`}
                    onClick={() => navigate(`/app/opportunities/${n.id}`)}
                    style={{ cursor: "pointer" }}>
                  <td><ScoreBadge score={n.fit?.score} band={n.fit?.band} insufficient={n.fit?.insufficient_data} /></td>
                  <td>
                    <Link to={`/app/opportunities/${n.id}`} className="font-medium hover:underline"
                          onClick={(e) => e.stopPropagation()}>
                      <Highlight text={n.title} tokens={searchTokens} />
                    </Link>
                    <div className="mt-1 flex gap-1 flex-wrap">
                      <span onClick={(e) => e.stopPropagation()}>
                        <SourceTag source={n.source} url={n.source_url} />
                      </span>
                      {n.data_quality_warnings?.length > 0 && (
                        <span className="source-tag" title={n.data_quality_warnings.join("; ")}>
                          ⚠ {n.data_quality_warnings.length} note(s)
                        </span>
                      )}
                    </div>
                  </td>
                  <td><RegionPill r={n.region} /></td>
                  <td className="text-[13px]">
                    {n.buyer_name
                      ? <Highlight text={n.buyer_name} tokens={searchTokens} />
                      : <span style={{ color: "var(--parapet-text-secondary)" }}>Not published</span>}
                  </td>
                  <td className="value-mono text-[13px]">
                    {n.value_amount != null
                      ? formatGBP(n.value_amount, n.value_currency)
                      : <span style={{ color: "var(--parapet-text-secondary)" }}>—</span>}
                  </td>
                  <td className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>{n.stage || "—"}</td>
                  <td><DeadlineCell d={n.deadline} /></td>
                  <td>
                    <Link to={`/app/opportunities/${n.id}`} className="btn-ghost text-[12px]"
                          data-testid={`opportunity-open-${n.id}`}
                          onClick={(e) => e.stopPropagation()}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      <PageNav
        page={page} totalPages={totalPages}
        onPage={(p) => setPage(p)}
        countText={`Showing ${items.length === 0 ? 0 : offset + 1}–${offset + items.length} of ${total.toLocaleString()}`}
      />
    </div>
  );
}

function Highlight({ text, tokens }) {
  if (!text || !tokens || tokens.length === 0) return text || null;
  // Build a single case-insensitive regex of all escaped tokens (longest first
  // so substrings don't shadow longer overlapping matches).
  const sorted = [...tokens].filter(Boolean).sort((a, b) => b.length - a.length);
  if (sorted.length === 0) return text;
  const escaped = sorted.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const rx = new RegExp(`(${escaped.join("|")})`, "gi");
  // split() with a capturing group returns alternating [non-match, match, ...]
  // — odd indices are the captured matches.
  const parts = String(text).split(rx);
  return parts.map((p, i) =>
    i % 2 === 1
      ? <mark key={i} style={{ background: "#FFF1B8", color: "inherit", padding: "0 1px", borderRadius: 2 }}>{p}</mark>
      : <span key={i}>{p}</span>
  );
}

function DeadlineCell({ d }) {
  if (!d) return <span className="text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>Not published</span>;
  const days = daysUntil(d);
  return (
    <div>
      <div className="text-[12px]">{formatDate(d)}</div>
      {days != null && (
        <div className="text-[11px]" style={{ color: "var(--parapet-text-secondary)" }}>
          {days > 0 ? `${days}d` : days === 0 ? "Today" : "Passed"}
        </div>
      )}
    </div>
  );
}

function RegionPill({ r }) {
  const color = {
    UK:   { bg: "#F4F4F4", fg: "#111111" },
    EU:   { bg: "#EEF3FF", fg: "#1A4FB3" },
    NATO: { bg: "#FFF5EC", fg: "#8C4A0B" },
    US:   { bg: "#EFFFEF", fg: "#0E5C2F" },
    FVEY: { bg: "#FBEFFF", fg: "#642396" },
    Other:{ bg: "#F8F8F8", fg: "#666" },
  }[r] || { bg: "#F8F8F8", fg: "#666" };
  return (
    <span className="value-mono text-[11px]"
          style={{ background: color.bg, color: color.fg, padding: "2px 7px", borderRadius: 3, letterSpacing: 0.3 }}>
      {r || "—"}
    </span>
  );
}

function PageNav({ page, totalPages, onPage, countText }) {
  // Compute compact page list: 1 … current-1, current, current+1 … last
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push("…");
    pages.push(totalPages);
  }
  return (
    <div className="flex items-center justify-between mt-4 text-[12px]" style={{ color: "var(--parapet-text-secondary)" }}>
      <span>{countText}</span>
      <div className="flex items-center gap-1">
        <button className="btn-ghost" disabled={page === 1}
                onClick={() => onPage(Math.max(1, page - 1))}
                data-testid="opportunities-page-prev">
          <ChevronLeft size={12} /> Prev
        </button>
        {pages.map((p, idx) =>
          p === "…" ? (
            <span key={`e${idx}`} className="px-2" style={{ color: "var(--parapet-text-secondary)" }}>…</span>
          ) : (
            <button key={p}
                    className={p === page ? "btn-ghost" : "btn-ghost"}
                    style={p === page ? { background: "#111", color: "#fff", borderColor: "#111" } : {}}
                    onClick={() => onPage(p)}
                    data-testid={`opportunities-page-${p}`}>
              {p}
            </button>
          )
        )}
        <button className="btn-ghost" disabled={page >= totalPages}
                onClick={() => onPage(Math.min(totalPages, page + 1))}
                data-testid="opportunities-page-next">
          Next <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}
